import type { Combinations } from "./combinations";
import { previewSku } from "./combinations";
import type { ErrorTarget, ProductForm } from "./editorModel";
import { draftFor, editorView } from "./editorModel";

const escapeRegExp = (text: string) => text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * Where a refused save belongs, so the editor can show it on that row or section as well as in the alert:
 * - "SKU PAD-TSH-01-BLK-M is already used by Clash Tee" → that SKU's row (typed SKU, or its preview when blank)
 * - "Add a photo for Black" → Black's photos
 * - "Stock for Black · M changed …" / "Price for Black · M …" → that row
 * - otherwise the section or field its words point at, or null (the alert alone)
 */
export function locateSaveError(message: string, input: ProductForm, combos: Combinations): ErrorTarget | null {
  // As the editor shows it: a carried row keeps its old SKU
  const form = editorView(input, combos);
  const text = (message || "").trim();
  if (!text) return null;
  const productCode = form.code.trim();

  const sku = /\bSKU\s+([A-Z0-9-]+)/i.exec(text)?.[1]?.toUpperCase();
  if (sku) {
    const row = combos.skus.find(
      (r) => (draftFor(form, r.key).sku.trim().toUpperCase() || previewSku(productCode, r.values, r.size)) === sku
    );
    if (row) return { kind: "sku", key: row.key };
  }

  const photo = /^Add a photo(?: for (.+?))?\.?$/i.exec(text);
  if (photo) {
    const label = photo[1]?.trim().toLowerCase();
    const style = label
      ? combos.styles.find((s) => s.label.toLowerCase() === label)
      : combos.styles.length === 1
      ? combos.styles[0]
      : undefined;
    return style ? { kind: "style", key: style.key } : { kind: "section", section: "photos" };
  }

  // Longest label first, so "Black · M" wins over "M"
  const byLength = [...combos.skus].sort((a, b) => b.label.length - a.label.length);
  const named = byLength.find((r) => new RegExp(`\\bfor ${escapeRegExp(r.label)}(?=$|[\\s.,:;—-])`, "i").test(text));
  if (named) return { kind: "sku", key: named.key };

  // "Switch on at least one combination…" is about the switches, not the picks
  if (/\bswitch on\b/i.test(text)) return { kind: "section", section: "combinations" };
  if (/combination/i.test(text)) return { kind: "section", section: "variants" };
  if (/\bSKU/i.test(text)) return { kind: "section", section: "combinations" };
  if (/photo/i.test(text)) return { kind: "section", section: "photos" };
  if (/\bcodes?\b/i.test(text)) return { kind: "field", field: "code" };
  if (/subcategor/i.test(text)) return { kind: "field", field: "subcategory" };
  if (/categor/i.test(text)) return { kind: "field", field: "category" };
  if (/price/i.test(text)) return { kind: "field", field: "price" };
  if (/\bname\b/i.test(text)) return { kind: "field", field: "name" };
  return null;
}

/** The server refused the save because orders moved a SKU's stock since the editor opened. */
export const isStockChangedError = (message: string): boolean => /^Stock for .+ changed\b.*since you opened/i.test((message || "").trim());
